import { HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoginResponse, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { isEqual } from 'lodash-es';
import { EMPTY, firstValueFrom, forkJoin, from, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, first, map, switchMap } from 'rxjs/operators';
import { AuthHandler, authLog } from 'src/app/auth/auth.handler';
import { TranslationKeys } from 'src/app/contracts';
import { ICoreUser, IDictionary, IHandleLoginOptions, ILoginResult, InternalError, RETURN_URL } from 'src/app/core/contracts';
import { Logger } from 'src/app/core/helpers/logger';
import { setStorageItem } from 'src/app/core/helpers/storage';
import { UI } from 'src/app/core/helpers/ui';
import { toRouterPath } from 'src/app/core/helpers/utils';

@Injectable()
export class Auth {
  private oidc = inject(OidcSecurityService);
  private router = inject(Router);
  private ui = inject(UI);
  private logger = inject(Logger);
  private eventService = inject(PublicEventsService);
  private authHandler = inject(AuthHandler);

  private shouldPromptForLogin = true;
  public async checkAuth(url?: string): Promise<LoginResponse | null> {
    authLog(`auth.flow::=>auth.checkAuth: checking auth flow..`, { url });
    try {
      return await firstValueFrom(this.authHandler.checkAuth(url));
    } catch (err) {
      this.logger.error({ msg: '@auth: Failed to check auth', err });
      return null;
    }
  }
  get isReady$(): Observable<boolean> {
    return this.authHandler.isReady$;
  }
  get user$(): Observable<ICoreUser> {
    return this.oidc.userData$.pipe(
      map(r => r.userData as ICoreUser),
      /* without the distinct oidc will keep emitting userData whenever it refreshes the session
         this causes redundant calls to 'configs/error-tracking', 'notifications/new-notifications-count', and every place that's listening to user$ */
      distinctUntilChanged((prev, curr) => isEqual(prev, curr))
    );
  }
  public get isAuthenticated$() {
    return this.isReady$.pipe(
      filter(ready => ready),
      first(),
      switchMap(() => this.oidc.isAuthenticated$),
      map(result => result.isAuthenticated)
    );
  }
  public async isAuthenticated() {
    try {
      return await firstValueFrom(this.isAuthenticated$);
    } catch (e) {
      return await Promise.resolve(false);
    }
  }
  public getAuthToken() {
    return this.oidc.getAccessToken();
  }
  public login(returnUrl?: string, username?: string) {
    authLog(`auth.flow::=>auth.login:`, { returnUrl, username });
    if (returnUrl) {
      sessionStorage.setItem(RETURN_URL, returnUrl);
    } else {
      const url = window.location.href.replace(/\/$/, ''); // remove trailing slash
      authLog(`auth.flow::=>auth.login: setting return url..`, { href: window.location.href, routerUrl: this.router.url, origin: window.origin });
      if (url !== window.origin) {
        // shouldn't set a return url for the default route (path: '' or '/') as the auth server will already redirect to it
        sessionStorage.setItem(RETURN_URL, window.location.href);
      }
    }
    authLog('auth.flow::=>auth.login: commencing login flow with auth handler');
    return this.authHandler.login(username);
  }
  public async logout() {
    return await firstValueFrom(this.authHandler.logout());
  }
  public getRoles(forceUpdate?: boolean) {
    return this.authHandler.getRoles(forceUpdate);
  }

  public getAgencyName(translate: TranslateService) {
    return this.oidc.getPayloadFromAccessToken().pipe(
      map((claims: IDictionary<unknown>) => {
        if (!claims) {
          return '';
        }
        const nameClaim = (translate.currentLang == 'ar' ? claims['NameAr'] : claims['NameEn']) as string | string[] | undefined;
        if (!nameClaim) {
          return '';
        }
        if (Array.isArray(nameClaim)) {
          return (nameClaim[0] || '').toString();
        }
        return nameClaim.toString();
      })
    );
  }
  public setAuthHeader(request: HttpRequest<unknown>, isApiUrl: boolean) {
    return this.getAuthToken().pipe(
      map(token => {
        if (token && isApiUrl) {
          return request.clone({
            setHeaders: { authorization: 'Bearer ' + token },
          });
        }
        return request;
      })
    );
  }
  public handleLogin({ tmpData, redirectTo, replaceUrl = true, promptToLogin }: IHandleLoginOptions = {}): Observable<ILoginResult> {
    /* NOTE: when calling handleLogin from new request scenarios redirectTo should NOT be set
       on native the user would still be on the request page so no redirection needed just follow on the request submission flow
       on web this.login() will auto save the current url and ReturnUrlGuard will handle redirecting the user to the request page
       
       for other scenarios e.g: needing to forward the user to a target page after completing login, in such case set redirectTo */
    return this.isAuthenticated$.pipe(
      first(),
      switchMap(authenticated => {
        authLog(`auth.flow::=>auth.handleLogin: isAuthenticated '${authenticated}'`, { redirectTo, replaceUrl });
        if (authenticated) {
          return of({ isAuthenticated: true });
        }
        return this.promptToLogin(promptToLogin === undefined ? this.shouldPromptForLogin : promptToLogin).pipe(
          switchMap(() => this.saveTmpData(tmpData)),
          switchMap(() => this.login(redirectTo)),
          // the code section below won't execute on the web because the page would have been redirected to the auth server inside the login method
          switchMap(result => {
            throw new InternalError('Invalid authentication flow'); // this should never happen, but just in case
          })
        );
      })
    );
  }
  promptToLogin(shouldPromptForLogin: boolean): Observable<void> {
    authLog(`auth.flow::=>auth.promptLogin:`, { shouldPromptForLogin });
    if (shouldPromptForLogin) {
      return this.ui.showConfirm$(TranslationKeys.DomainCommon.PleaseLoginToContinue).pipe(
        switchMap(yes => {
          if (yes) {
            return of(undefined as void);
          }
          return EMPTY;
        })
      );
    } else {
      return of(undefined as void);
    }
  }
  saveTmpData(tmpData?: IDictionary<unknown>): Observable<void> {
    if (!tmpData) {
      return of(undefined as void); // emit undefined so as to not break the observable chain
    }
    authLog(`auth.flow::=>auth.saveTmpData:`, { tmpData });
    const storagePromises = Object.entries(tmpData).map(([key, data]) => setStorageItem(key, data));
    return from(Promise.all(storagePromises)).pipe(map(() => {}));
  }
  getReturnUrl() {
    return this.isAuthenticated$.pipe(
      first(),
      switchMap(isAuthenticated => {
        return forkJoin({
          isAuthenticated: of(isAuthenticated),
          roles: isAuthenticated ? this.getRoles(true) : of([] as string[]), // force update roles to ensure we get the latest claims (since the access token would still have the old claims)
        });
      }),
      map(({ isAuthenticated, roles }) => {
        const returnUrl = sessionStorage.getItem(RETURN_URL);
        authLog(`auth.flow::=>auth.getReturnUrl:`, { isAuthenticated, returnUrl, href: window.location.href, routerUrl: this.router.url, roles });
        if (isAuthenticated) {
          if (returnUrl) {
            sessionStorage.removeItem(RETURN_URL);
            const routeUrl = toRouterPath(returnUrl); // convert absolute urls (if any) to a router friendly path
            authLog(`auth.flow::=>auth.getReturnUrl: successful login, removed return url`, {
              returnUrl,
              href: window.location.href,
              routerUrl: this.router.url,
              routeUrl,
            });
            if (routeUrl !== this.router.url) {
              authLog(`auth.flow::=>auth.getReturnUrl: successful login, current url (router.url) is different from return url (routeUrl)`);
              return routeUrl;
            } else {
              authLog(`auth.flow::=>auth.getReturnUrl: successful login, current url (router.url) is same as return url (routeUrl), skipping navigation`);
              return null; // return null to indicate no redirection
            }
          }
        } else {
          if (returnUrl) {
            sessionStorage.removeItem(RETURN_URL);
            authLog(`auth.flow::=>auth.getReturnUrl: unauthenticated, removed return url`, { returnUrl });
            /* e.g: user clicked Close on the login page
              shouldn't attempt redirecting to RETURN_URL in such case, because otherwise if the return url had [AuthGuard] applied, another redirection to auth server will occur */
          } else {
            authLog(`auth.flow::=>auth.getReturnUrl: unauthenticated, no return url to remove`);
          }
        }

        return null; // return null to indicate no redirection
      })
    );
  }
  refreshSession() {
    return this.isAuthenticated$.pipe(
      first(),
      switchMap(isAuthenticated => {
        if (isAuthenticated) {
          authLog(`auth.flow::=>auth.refreshSession: refreshing session`);
          return this.oidc.forceRefreshSession().pipe(
            map(response => {
              authLog(`auth.flow::=>auth.refreshSession: session refreshed`, { response });
              return true;
            })
          );
        }
        authLog(`auth.flow::=>auth.refreshSession: not authenticated, cannot refresh session`);
        return of(false);
      })
    );
  }
}
